import type { Salon } from "@/data/types";

export const CENTRAL_PHONE = "+46812345670";
export const CENTRAL_PHONE_DISPLAY = "08-123 456 70";



/**
 * Appends BokaDirekt attribution parameters to a booking URL.
 *
 * These UTMs are for BokaDirekt's own reporting — GA4 attribution is handled by
 * the cross-domain linker in `load-analytics.ts`, not by these params.
 *
 * Existing query strings are preserved, and any UTM already present on the URL
 * is left untouched (the /barberare-stockholm-ahlens-city/* ad routes ship
 * their own campaign UTMs and must not be overwritten).
 *
 * Never apply this to URLs used in `sameAs` structured data — canonical
 * profile URLs must stay free of tracking parameters.
 */
export function withBookingUtm(url: string): string {
  if (!url || !url.includes("bokadirekt.se")) return url;
  if (url.includes("utm_source=")) return url;
  const [base, hash = ""] = url.split("#");
  const separator = base.includes("?") ? "&" : "?";
  const params = "utm_source=tailorsbarber.se&utm_medium=referral&utm_campaign=website";
  return `${base}${separator}${params}${hash ? `#${hash}` : ""}`;
}

/**
 * Chain-level BokaDirekt search result listing all four Tailor's Barber salons.
 * Use this for generic CTAs (homepage, service pages, blog, guides, products).
 * Salon-specific pages MUST use `bookingUrlForSalon(salon)` instead.
 */
export const GENERIC_BOKADIREKT = withBookingUtm("https://www.bokadirekt.se/sok?q=tailors+barber");
export const REVIEW_COUNT_VALUE = "5000+";
export const REVIEW_COUNT_TEXT = `${REVIEW_COUNT_VALUE} nöjda kunder`;

/** Salon booking link for use in hrefs — carries BokaDirekt attribution UTMs. */
export function bookingUrlForSalon(salon: Pick<Salon, "bokaDirektUrl">) {
  return withBookingUtm(salon.bokaDirektUrl);
}


/**
 * Returns the per-barber BokaDirekt deep link for a given salon, or the salon
 * fallback if provided. Returns undefined if neither exists — callers must
 * hide the booking button in that case.
 */
export function bookingUrlForBarberAt(
  bookingUrlBySalon: Partial<Record<Salon["slug"], string>>,
  salonSlug: Salon["slug"],
  fallback?: string,
): string | undefined {
  return bookingUrlBySalon[salonSlug] ?? fallback;
}

/** Formatted "from" price, with BokaDirekt fallback when price is missing. */
export function formatPriceFrom(priceFrom?: number) {
  return typeof priceFrom === "number"
    ? `Från ${priceFrom} kr`
    : "Se pris på BokaDirekt";
}
